<script setup lang="ts">
    import { useI18n } from 'vue-i18n';
    import { Label } from '@/components/ui/label';
    import { Input } from '@/components/ui/input';
    import { computed } from 'vue';
    import type { ChangedFields, Employee } from '@/types';

    const { t } = useI18n();

    const props = defineProps<{
        name: string;
        data?: ChangedFields;
        oldValue?: string | number | undefined;
        realValue?: string | number | undefined;
        updateValue?: string | undefined;
        employee?: Employee;
        group: string;
        groupKey: string;
        translation?: string;
    }>();

    const noChangesRegistered = (v: string | number | undefined) => v === 'no_change';

    const old = computed(() => {
        return props.oldValue ?? props.data?.old?.[props.name]?.value ?? null;
    });

    const real = computed(() => {
        return (
            props.realValue ??
            props.employee?.[props.groupKey]?.[props.name] ??
            props.employee?.[props.name] ??
            null
        );
    });

    const oldVal = computed(() => {
        if (old.value === null || old.value === '') return 'no_change';
        return old.value != real.value ? old.value : 'no_change';
    });

    const realVal = computed(() => {
        if (real.value === 0 || real.value === '0' || real.value === null || real.value === '')
            return '-';
        return real.value;
    });

    const dateVal = computed(() => {
        if (old.value === null || old.value === '' || old.value === real.value) return 'no_change';
        const updatedAt =
            props.updateValue ?? props.data?.attributes?.[props.name]?.updated_at ?? null;
        return updatedAt === null || updatedAt === '' ? 'no_change' : updatedAt;
    });
</script>

<template>
    <div class="flex flex-col gap-2">
        <div class="text-secondary border-secondary border-b pb-2 text-xs font-bold">
            {{ translation ?? t(`employee.${groupKey}.${name}`) }}
        </div>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div class="flex flex-col gap-2">
                <Label class="text-muted-foreground">{{ t('forms.from') }}</Label>
                <div class="relative flex items-center gap-2">
                    <Input
                        :model-value="
                            noChangesRegistered(oldVal)
                                ? t('employee.changes.no_changes_registered')
                                : oldVal
                        "
                        :class="noChangesRegistered(oldVal) ? 'text-faded-foreground' : ''"
                        :disabled="true"
                    />
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <Label class="text-muted-foreground">{{ t('forms.to') }}</Label>
                <div class="relative flex items-center gap-2">
                    <Input
                        :model-value="
                            noChangesRegistered(realVal)
                                ? t('employee.changes.no_changes_registered')
                                : realVal
                        "
                        :class="noChangesRegistered(realVal) ? 'text-faded-foreground' : ''"
                        :disabled="true"
                    />
                </div>
            </div>
            <div class="flex flex-col gap-2">
                <Label class="text-muted-foreground">{{ t('employee.changes.change_date') }}</Label>
                <Input
                    :model-value="
                        noChangesRegistered(dateVal)
                            ? t('employee.changes.no_changes_registered')
                            : dateVal
                    "
                    :class="noChangesRegistered(dateVal) ? 'text-faded-foreground' : ''"
                    :disabled="true"
                />
            </div>
        </div>
    </div>
</template>
